import { ReactNode, useState } from 'react';
import { Button, NavTabs, TabPanels, type NavTab } from '@sib/ui';
import { DetailsViewActionButton } from './DetailsViewActionButton';
import type { DetailsViewActionConfig } from './DetailsView.types';

interface DetailsViewProps<TTab extends string> {
  tabs: NavTab<TTab>[];
  fallbackTab: TTab;
  panels: Partial<Record<TTab, ReactNode>>;
  onClose: () => void;
  onEdit: () => void;
  closeLabel: string;
  editLabel: string;
  editDisabled?: boolean;
  editDisabledTooltip?: string;
  /** When set, Close/Edit actions are shown only for these tabs. */
  actionsVisibleFor?: TTab[];
  /** Hides the Close button in the actions row. Defaults to shown. */
  hideClose?: boolean;
  /** When set, shows a back button before other actions (e.g. return to previous entity). */
  onBack?: () => void;
  backLabel?: string;
  /**
   * Per-tab override for the primary action button (defaults to Edit).
   * Pass `null` to hide the primary action and keep only Close.
   */
  primaryAction?: Partial<Record<TTab, DetailsViewActionConfig | null>>;
  /** Notified when the active tab changes (e.g. to sync the URL). Doesn't control the display. */
  onTabChange?: (tab: TTab) => void;
  actions?: ReactNode;
}

export const DetailsView = <TTab extends string>({
  tabs,
  fallbackTab,
  panels,
  onClose,
  onEdit,
  closeLabel,
  editLabel,
  editDisabled = false,
  editDisabledTooltip,
  actionsVisibleFor,
  hideClose,
  onBack,
  backLabel,
  primaryAction,
  onTabChange,
  actions,
}: DetailsViewProps<TTab>) => {
  const [activeTab, setActiveTab] = useState(fallbackTab);
  const handleTabChange = (tab: TTab) => {
    setActiveTab(tab);
    onTabChange?.(tab);
  };
  const shouldShowActions =
    !actionsVisibleFor || actionsVisibleFor.includes(activeTab);

  const tabPrimaryAction = primaryAction?.[activeTab];
  const activePrimaryAction: DetailsViewActionConfig | null =
    tabPrimaryAction === undefined
      ? {
          label: editLabel,
          onClick: onEdit,
          disabled: editDisabled,
          disabledTooltip: editDisabledTooltip,
        }
      : tabPrimaryAction;

  const showActionsRow =
    shouldShowActions && (!hideClose || !!activePrimaryAction || !!onBack);

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col gap-6">
      <div className="flex min-h-0 w-full flex-1 flex-col gap-4">
        <NavTabs value={activeTab} tabs={tabs} onChange={handleTabChange} />
        <div className="relative min-h-0 flex-1">
          <div className="absolute inset-0 overflow-y-auto">
            <TabPanels
              value={activeTab}
              fallback={fallbackTab}
              panels={panels}
            />
          </div>
        </div>
      </div>

      {showActionsRow && (
        <div className="flex shrink-0 justify-end gap-2">
          {onBack && backLabel && (
            <Button type="button" variant="outline" onClick={onBack}>
              {backLabel}
            </Button>
          )}
          {!hideClose && (
            <Button type="button" variant="outline" onClick={onClose}>
              {closeLabel}
            </Button>
          )}
          {actions}
          {activePrimaryAction && (
            <DetailsViewActionButton {...activePrimaryAction} />
          )}
        </div>
      )}
    </div>
  );
};
